import React, { useState, useEffect, useRef } from 'react';
import './BackendSearchSelect.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import validate from '../../common/validate';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import logComponent from '../../common/logComponent';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';

export default function BackendSearchSelect({ onValidate, selectedItems: propsSelectedItems, selectedItemsError, selectedItemsLoading, onSelect, searchFunction, name, label, itemKeyField, itemPluralTerm, instructions, maximumSelections, showError, errorMessage, rules, itemComponent, selectedItemComponent, potentialItemComponent, itemComponentPropName, displayField }) {
  const stateSelectedItems = propsSelectedItems ? propsSelectedItems : [];
  const [potentialItems, setPotentialItems] = useState(null);
  const [potentialItemsLoading, setPotentialItemsLoading] = useState(false);
  const [potentialItemsError, setPotentialItemsError] = useState(false);
  const [selectedItems, setSelectedItems] = useState(stateSelectedItems);
  const [inputValue, setInputValue] = useState('');
  const [searchTermsUpdateTimeout, setSearchTermsUpdateTimeout] = useState(null);
  const [searchTerms, setSearchTerms] = useState('');
  const [showList, setShowList] = useState(false);
  const [validationDetails, setValidationDetails] = useState(null);
  const [isValid, setIsValid] = useState(null);
  const [selectionChanged, setSelectionChanged] = useState(false);
  const [requiresValidation, setRequiresValidation] = useState(false);
  const selectedItemsRef = useRef(propsSelectedItems ? propsSelectedItems : []);

  const inputRef = useRef(null);
  const overlayRef = useRef(null);

  const setInputRef = el => { inputRef.current = el; }

  const setOverlayRef = el => { overlayRef.current = el; }

  const setSelectedItemsRef = el => {
    selectedItemsRef.current = el;
    setSelectedItems(el);
  }

  useEffect(() => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect onMount'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect useEffect [requiresValidation]'); }
    if (requiresValidation === true) {
      validateThis();
      setRequiresValidation(false);
    }
  }, [requiresValidation]);

  useEffect(() => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect useEffect [selectedItemsLoading]'); }
    if (!propsSelectedItems) return;
    // handle change in selectedItems loading state or 
    if (!selectedItemsError) {
      setSelectedItemsRef(propsSelectedItems.map(item => item))
      setRequiresValidation(true);
    }
  }, [selectedItemsLoading]);

  useEffect(() => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect useEffect [propsSelectedItems?.length]'); }
    if (!propsSelectedItems) return;
      setSelectedItemsRef(propsSelectedItems.map(item => item))
      setRequiresValidation(true);
  }, [propsSelectedItems?.length]);

  useEffect(() => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect useEffect [searchTerms]'); }
    handleSearchTermsChange();
  }, [searchTerms]);

  useEffect(() => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect useEffect [selectedItems]'); }
    setPotentialItems(potentialItems ?
      potentialItems.filter(item => !selectedItems.some(selectedItem => selectedItem[itemKeyField] === item[itemKeyField])) :
      null);
  }, [selectedItems?.length]);

  useEffect(() => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect useEffect [selectionChanged]'); }
    if (selectionChanged === true) handleSelectionChange();
  }, [selectionChanged]);


  const handleSearchTermsChange = () => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect handleSearchTermsChange'); }

    if (searchTerms.length < 2) {
      setShowList(false);
      setPotentialItems(null);
    } else {
      loadItems();
    } 

    if (overlayRef.current) overlayRef.current.scrollTo(0,0);
  }

  const loadItems = () => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect loadItems'); }
    setPotentialItems(null);
    setPotentialItemsLoading(true);
    setPotentialItemsError(false);
    setShowList(true);

    searchFunction(inputValue)
      .then(
        result => {
          const newPotentialItems = result.filter(item => !selectedItems.some(selectedItem => item[itemKeyField] === selectedItem[itemKeyField]));

          setPotentialItems(newPotentialItems);
          setPotentialItemsLoading(false);
          setPotentialItemsError(false);
        },
        error => {
          setPotentialItems(null);
          setPotentialItemsLoading(false);
          setPotentialItemsError(true);
        }
      );

  }

  const readNestedField = (obj, nestedField) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect readNestedField'); }
    const nestedFieldArray = nestedField.split('.');

    if (!nestedFieldArray) return null;

    return nestedFieldArray.reduce((prev, curr) => {
      return prev[lowerCaseFirstLetter(curr)];
    }, obj);

  }

  const handleKeyDown = (e) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect handleKeyDown: e=' + e); }
    const { key } = e;

    if (key && key === 'Escape') {
      setShowList(false);
      setInputValue('');
      setSearchTerms('');
    }
  }

  const handleInputValueChange = () => {
    //TODO:  What to put here.  Method was missing
  }

  const clearInput = () => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect clearInput'); }
    setInputValue('');
    inputRef.current.focus();
  }

  const updateValue = (e) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect updateValue'); }
    if (searchTermsUpdateTimeout) clearTimeout(searchTermsUpdateTimeout);
    setInputValue(e.target.value);
    setSearchTermsUpdateTimeout(setTimeout(updateSearchTerms, 350));
  }

  const updateSearchTerms = () => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect updateSearchTerms'); }
    setSearchTerms(inputValue);
    setSearchTermsUpdateTimeout(null);
  }

  const addItem = (e) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect addItem'); }
    const { itemUniqueKey } = e.currentTarget.dataset;

    // if no item with this key is already selected, then add it to selectedItems array 
    if (!selectedItems.some(item => item[itemKeyField] === itemUniqueKey)) {
      setSelectedItemsRef(selectedItems.concat(potentialItems.find(item => item[itemKeyField] == itemUniqueKey))); // eslint-disable-line
      setShowList(false);
      setInputValue('');
      setSearchTerms('');
      setSelectionChanged(true);
    }
  }

  const removeItem = (e) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect removeItem'); }
    const { itemUniqueKey } = e.currentTarget.dataset;

    setSelectedItemsRef(selectedItemsRef.current.filter(item => item[itemKeyField] != itemUniqueKey)); // eslint-disable-line
    setSelectionChanged(true);
  }

  const handleSelectionChange = () => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect handleSelectionChange'); }

    if (onSelect) onSelect(selectedItemsRef.current);  

    validateThis();

    // Reset the selectionChanged state to false after handling
    setSelectionChanged(false);
  }

  const isSelected = (item) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect isSelected'); }

    return selectedItems.some(selectedItem => selectedItem === item);
  }

  const validateThis = () => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect validateThis'); }

    let validationDetails = [];
    for (let rule in rules) {
      validationDetails.push({
        rule: rule,
        valid: validate[rule](selectedItems, rules[rule])
      })
    }

    let isValid = validationDetails.reduce((prev, curr) => prev && curr.valid, true);

    setValidationDetails(validationDetails);
    setIsValid(isValid);

    onValidate({
      name: name,
      isValid: isValid,
      value: selectedItemsRef.current
    });
  }

  const getPotentialItemComponent = (item) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect getPotentialItemComponent'); }

    const PotentialItemComponent = potentialItemComponent ? potentialItemComponent : itemComponent;

    if (PotentialItemComponent) {
      let itemComponentProp = {};
      itemComponentProp[itemComponentPropName] = item;

      return <PotentialItemComponent {...itemComponentProp} />;
    } else if (displayField && item[displayField]) {
      return <p className="Backend-Search-Select__generic-display-item">{ item[displayField] }</p>;
    } else {
      return null;
    }
  }

  const getSelectedItemComponent = (item) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect getSelectedItemComponent'); }

    const SelectedItemComponent = selectedItemComponent ? selectedItemComponent : itemComponent;

    if (SelectedItemComponent) {
      let itemComponentProp = {};
      itemComponentProp[itemComponentPropName] = item;

      return <SelectedItemComponent {...itemComponentProp} />;
    } else if (displayField && item[displayField]) {
      return <p className="Backend-Search-Select__generic-display-item">{ item[displayField] }</p>;
    } else {
      return null;
    }    
  }

  const hideList = () => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect hideList'); }
    setShowList(false);
    setInputValue('');
    setPotentialItems(null);
  }

  const stopEvent = (e) => {
    if (logComponent('BackendSearchSelect')) { console.debug('BackendSearchSelect stopEvent'); }
    e.stopPropagation();
  }


  return (
    <div 
      className="Backend-Search-Select"
      onClick={hideList}>
      {
        showError && isValid === false &&
        <p className="Select-Input-Group__error">{ errorMessage }</p>
      }
      <label 
        className="Backend-Search-Select__label"
        htmlFor={name + '-multi-select'} >
              {label}
      {
        rules && rules.required &&
        <span className="Backend-Search-Select__required"> *</span>
      }
      </label>
      <div 
        className="Backend-Search-Select__body">
        <div className="Backend-Search-Select__input-wrapper">
          <FontAwesomeIcon className="Backend-Search-Select__input-icon" icon="search" />
          <input 
            placeholder={!maximumSelections || maximumSelections > selectedItems.length ? 'Search ' + itemPluralTerm : `Maximum number of ${itemPluralTerm} selected.`}
            readOnly={maximumSelections && maximumSelections <= selectedItems.length }
            className={'Backend-Search-Select__input' + 
                        (showList ? ' Backend-Search-Select__input--visible' : '') +
                        (maximumSelections && maximumSelections <= selectedItems.length ? ' Backend-Search-Select__input-disabled' : '')}
            value={inputValue}
            onClick={stopEvent}
            onChange={updateValue}
            onFocus={handleInputValueChange}
            onKeyDown={handleKeyDown}
            ref={setInputRef} />
        </div>
        <ul 
          ref={setOverlayRef}
          className={'Backend-Search-Select__selection-overlay' + (showList ? '--visible' : '')}
          onClick={stopEvent}
          >
        {
          potentialItems &&
          (potentialItems.length === 0 ?
          <li key="noMatches" className="Backend-Search-Select__empty-message">No matches found...</li> :
          potentialItems.map(item => {
            return <li 
              className="Backend-Search-Select__potential-item"
              key={item[itemKeyField]}>
                { getPotentialItemComponent(item) }
              <button 
                tabIndex="-1"
                type="button"
                className={'Backend-Search-Select__add-item-btn' + (isSelected(item) ? ' Backend-Search-Select__add-item-btn--selected' : '')}
                onClick={addItem}
                data-item-unique-key={item[itemKeyField]}>
                <FontAwesomeIcon icon="plus" />
              </button>
            </li>
          }))
        }
        {
          (potentialItemsLoading || potentialItemsError) &&
          <li key="loadingMessage" className="Backend-Search-Select__loading-message">
            <ModuleLoadingStatus isLoading={potentialItemsLoading} isError={potentialItemsError} reloadAction={loadItems} />
          </li>
        }  
        </ul>
      {
        selectedItems.length > 0 &&
        <ul className="Backend-Search-Select__selected-items">  
        {
          selectedItems
            .map(item => {
            return <li 
              className="Backend-Search-Select__selected-item"
              key={item[itemKeyField]}>
                { getSelectedItemComponent(item) }
              <button
                type="button"
                className="Backend-Search-Select__remove-item-btn"
                onClick={removeItem}
                data-item-unique-key={item[itemKeyField]}>
                <FontAwesomeIcon icon="times" />  
              </button>
            </li>
          })
        }
        </ul>
      }
      </div>
      <p className="Backend-Search-Select__instructions">{ instructions }</p>
    </div>
  );
}